import React, { useState, useEffect } from 'react';
import { useUserData } from '../contexts/UserDataContext';
import { useAuth } from '../contexts/AuthContext';
import ItemCard from './ItemCard';
import { api } from '../services/api';
import { ProgressBar } from './UserDashboard'; // Use named import

const categories = [
  { key: 'low', label: 'Beginner level' },
  { key: 'medium', label: 'Intermediate level' },
  { key: 'high', label: 'Advanced level' },
];

type DictionaryWord = {
  en: string; // the word itself
  hi: string;
  bn: string;
  mr: string;
  te: string;
  ta: string;
  gu: string;
  example: string; // the English example sentence
  [key: string]: string;
};

// Sample/mock data for dictionary words with all supported languages
const sampleWords: Record<string, DictionaryWord[]> = {
  low: [
    { en: 'Book', hi: 'किताब', bn: 'বই', mr: 'पुस्तक', te: 'పుస్తకం', ta: 'புத்தகம்', gu: 'પુસ્તક', example: 'I read a book.' },
    { en: 'Water', hi: 'पानी', bn: 'জল', mr: 'पाणी', te: 'నీరు', ta: 'தண்ணீர்', gu: 'પાણી', example: 'Drink water.' },
    { en: 'Sun', hi: 'सूरज', bn: 'সূর্য', mr: 'सूर्य', te: 'సూర్యుడు', ta: 'சூரியன்', gu: 'સૂર્ય', example: 'The sun is bright.' },
    { en: 'Pen', hi: 'कलम', bn: 'কলম', mr: 'पेन', te: 'కలం', ta: 'பேனா', gu: 'પેન', example: 'This is my pen.' },
    { en: 'Chair', hi: 'कुर्सी', bn: 'চেয়ার', mr: 'खुर्ची', te: 'కుర్చీ', ta: 'நாற்காலி', gu: 'ખુરશી', example: 'Sit on the chair.' },
    { en: 'Dog', hi: 'कुत्ता', bn: 'কুকুর', mr: 'कुत्रा', te: 'కుక్క', ta: 'நாய்', gu: 'કૂતરો', example: 'The dog is barking.' },
    { en: 'Apple', hi: 'सेब', bn: 'আপেল', mr: 'सफरचंद', te: 'ఆపిల్', ta: 'ஆப்பிள்', gu: 'સફરજન', example: 'I ate an apple.' },
  ],
  medium: [
    { en: 'Journey', hi: 'यात्रा', bn: 'ভ্রমণ', mr: 'प्रवास', te: 'ప్రయాణం', ta: 'பயணம்', gu: 'પ્રવાસ', example: 'The journey was long.' },
    { en: 'Brave', hi: 'बहादुर', bn: 'সাহসী', mr: 'धाडसी', te: 'ధైర్యవంతుడు', ta: 'தைரியசாலி', gu: 'સાહસી', example: 'He is a brave boy.' },
    { en: 'Silent', hi: 'चुप', bn: 'নীরব', mr: 'शांत', te: 'నిశ్శబ్దం', ta: 'அமைதி', gu: 'શાંત', example: 'The room was silent.' },
    { en: 'Garden', hi: 'बगीचा', bn: 'বাগান', mr: 'बाग', te: 'తోట', ta: 'தோட்டம்', gu: 'બગીચો', example: 'Children play in the garden.' },
    { en: 'Window', hi: 'खिड़की', bn: 'জানালা', mr: 'खिडकी', te: 'కిటికీ', ta: 'ஜன்னல்', gu: 'ખિડકી', example: 'Open the window.' },
    { en: 'River', hi: 'नदी', bn: 'নদী', mr: 'नदी', te: 'నది', ta: 'ஆறு', gu: 'નદી', example: 'The river is wide.' },
    { en: 'Teacher', hi: 'शिक्षक', bn: 'শিক্ষক', mr: 'शिक्षक', te: 'ఉపాధ్యాయుడు', ta: 'ஆசிரியர்', gu: 'શિક્ષક', example: 'The teacher is kind.' },
  ],
  high: [
    { en: 'Accomplish', hi: 'पूरा करना', bn: 'সম্পন্ন করা', mr: 'पूर्ण करणे', te: 'సాధించు', ta: 'நிறைவேற்ற', gu: 'પૂર્ણ કરવું', example: 'She will accomplish her goals.' },
    { en: 'Magnificent', hi: 'शानदार', bn: 'দারুণ', mr: 'भव्य', te: 'అద్భుతమైన', ta: 'அற்புதமான', gu: 'શાનદાર', example: 'The view was magnificent.' },
    { en: 'Perseverance', hi: 'दृढ़ता', bn: 'অধ্যবসায়', mr: 'चिकाटी', te: 'ధైర్యం', ta: 'தொடர்ந்து முயற்சி', gu: 'હઠ', example: 'Perseverance leads to success.' },
    { en: 'Opportunity', hi: 'अवसर', bn: 'সুযোগ', mr: 'संधી', te: 'అవకాశం', ta: 'வாய்ப்பு', gu: 'અવસર', example: 'Take every opportunity.' },
    { en: 'Determination', hi: 'दृढ़ निश्चय', bn: 'দৃঢ় সংকল্প', mr: 'दृढ निश्चय', te: 'దృఢ సంకల్పం', ta: 'உறுதி', gu: 'દૃઢ નિશ્ચય', example: 'She showed great determination.' },
    { en: 'Achievement', hi: 'उपलब्धि', bn: 'অর্জন', mr: 'सिद्धी', te: 'సాధన', ta: 'சாதனை', gu: 'સિદ્ધિ', example: 'This is a big achievement.' },
    { en: 'Responsibility', hi: 'जिम्मेदारी', bn: 'দায়িত্ব', mr: 'जबाबदारी', te: 'బాధ్యత', ta: 'பொறுப்பு', gu: 'જવાબદારી', example: 'It is your responsibility.' },
  ],
};

interface DictionaryPageProps {
  selectedLanguage?: string;
}

export default function DictionaryPage({ selectedLanguage }: DictionaryPageProps) {
  const { userData, addCompletedWord, addFavoriteWord, getCompletedWords, refreshUserData } = useUserData();
  const actualSelectedLanguage = selectedLanguage || userData?.selectedLanguage || 'en';
  const [selectedCategory, setSelectedCategory] = useState(categories[0].key);
  const [items, setItems] = useState<any[]>([]);
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const { currentUser } = useAuth();
  const batchSize = 3;
  const userId = currentUser?.uid;
  const [completedIds, setCompletedIds] = useState<string[]>([]);
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
  const [wordTotal, setWordTotal] = useState(0);
  const [wordCompleted, setWordCompleted] = useState(0);
  const [page, setPage] = useState(1); // Track current API page
  const [limit] = useState(50); // Always fetch 50 at a time
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isApiLoading, setIsApiLoading] = useState(false); // Add loading state to prevent duplicate calls
  const [loadingButtons, setLoadingButtons] = useState<{ [key: string]: boolean }>({});
  const [completedCount, setCompletedCount] = useState(0); // Track completed count for live updates

  useEffect(() => {
    if (!userId || isApiLoading) return; // Prevent duplicate calls while loading

    if (!userId) {
      console.log('No userId available, using sample data');
      setItems([]); // Clear old data immediately
      setLoading(true);
      setTimeout(() => {
        setItems(sampleWords[selectedCategory] || []);
        setCurrentBatchIndex(0);
        setLoading(false);
      }, 0);
      return;
    }
    console.log('DictionaryPage: Fetching data for category:', selectedCategory, 'page:', page, 'userId:', userId);
    setItems([]); // Clear old data immediately
    setLoading(true);
    setIsApiLoading(true); // Set API loading state

    api.getMasterWords(selectedCategory, userId, page, limit)
      .then(res => {
        console.log('DictionaryPage: API response received for category:', selectedCategory);
        const items = res.items || [];
        setItems(items);
        setCurrentBatchIndex(0);
        setTotalCount(res.totalCount || 0);
        setTotalPages(res.totalPages || 1);

        // Auto-advance to next page if all items are completed/memorized
        if (items.length === 0 && page < (res.totalPages || 1)) {
          console.log('DictionaryPage: All items completed on page', page, '- advancing to next page');
          setPage(page + 1);
        }
      })
      .catch(err => {
        console.error('Error fetching words:', err);
        setItems(sampleWords[selectedCategory] || []);
        setCurrentBatchIndex(0);
      })
      .finally(() => {
        setLoading(false);
        setIsApiLoading(false); // Clear API loading state
      });

    // Remove the unnecessary count API call
    // api.getMasterWordsCount(selectedCategory)
    //   .then(count => {
    //     setTotalCount(count);
    //   })
    //   .catch(err => {
    //     console.error('Error fetching words count:', err);
    //     setTotalCount(0);
    //   });
  }, [selectedCategory, userId, page, limit]);

  useEffect(() => {
    getCompletedWords(selectedCategory).then(res => {
      const completed = res.map(w => w._id || '');
      setCompletedIds(completed);
      setCompletedCount(completed.length); // Set initial completed count
    });
    // Fetch favorite word IDs for this category
    if (userId) {
      api.getFavoriteWords(userId, selectedCategory).then(favs => {
        setFavoriteIds(Array.isArray(favs) ? favs.map(f => f.itemId) : []);
      });
    }
  }, [selectedCategory, userId]);

  // Remove the unnecessary count API calls
  // useEffect(() => {
  //   if (!userId) return;
  //   api.getMasterWordsCount('')
  //     .then(setWordTotal);
  //   api.getCompletedWordsCount(userId, '')
  //     .then(setWordCompleted);
  // }, [userId]);

  const batchStart = currentBatchIndex * batchSize;
  const batchEnd = batchStart + batchSize;
  const currentBatch = items.slice(batchStart, batchEnd);
  const filteredWords = currentBatch.filter(item => !completedIds.includes(item._id));

  const getTranslation = (item: any, language: string): string => {
    return item[language] || item.word;
  };

  // Add a function to check if a word is favorited
  const isWordFavorite = (item: any) => {
    return userData?.favoriteWords?.some(fav => fav.en === item.en);
  };

  const isFavorite = (item: any) => favoriteIds.includes(`word:${item._id}`);

  const percent = wordTotal > 0 ? Math.round((wordCompleted / wordTotal) * 100) : 0;

  // Next/Prev button handlers for API pagination
  const handleNext = () => {
    if (batchEnd < items.length) {
      setCurrentBatchIndex(i => i + 1);
    } else if (page < totalPages) {
      setPage(p => p + 1);
      setCurrentBatchIndex(0);
    }
  };

  const handlePrev = () => {
    if (currentBatchIndex > 0) {
      setCurrentBatchIndex(i => i - 1);
    } else if (page > 1) {
      setPage(p => p - 1);
      setCurrentBatchIndex(Math.floor((limit / batchSize)) - 1); // last batch of previous page
    }
  };

  const handleDone = async (item: any) => {
    if (!item._id || loadingButtons[`done-${item._id}`]) return;

    // Set loading state for this specific button
    setLoadingButtons(prev => ({ ...prev, [`done-${item._id}`]: true }));

    // Optimistically update UI immediately
    setItems(prev => prev.filter(i => i._id !== item._id));
    setCompletedCount(prev => prev + 1); // Increment completed count immediately

    try {
      // Run API calls in parallel for better performance
      await Promise.all([
        addCompletedWord(`word:${item._id}`, item.difficulty || 'low'),
        api.memorizeWord(userId!, `word:${item._id}`, item.difficulty || 'low', 'done'),
        refreshUserData()
      ]);
    } catch (error) {
      console.error('Error in handleDone:', error);
      // Revert optimistic update on error
      setItems(prev => [...prev, item]);
      setCompletedCount(prev => prev - 1); // Revert completed count on error
    } finally {
      setLoadingButtons(prev => ({ ...prev, [`done-${item._id}`]: false }));
    }
  };

  const handleMemorize = async (item: any) => {
    if (!item._id || loadingButtons[`memorize-${item._id}`]) return;

    // Set loading state for this specific button
    setLoadingButtons(prev => ({ ...prev, [`memorize-${item._id}`]: true }));

    // Optimistically update UI immediately
    setItems(prev => prev.filter(i => i._id !== item._id));
    setCompletedCount(prev => prev + 1); // Increment completed count immediately

    try {
      // Run API calls in parallel for better performance
      await Promise.all([
        api.memorizeWord(userId!, `word:${item._id}`, item.difficulty || 'low', 'memorized'),
        refreshUserData()
      ]);
    } catch (error) {
      console.error('Error in handleMemorize:', error);
      // Revert optimistic update on error
      setItems(prev => [...prev, item]);
      setCompletedCount(prev => prev - 1); // Revert completed count on error
    } finally {
      setLoadingButtons(prev => ({ ...prev, [`memorize-${item._id}`]: false }));
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 sm:p-4 md:p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight drop-shadow-lg mb-4 md:mb-6 text-center">
          <span className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">Essential Dictionary</span>
        </h1>

        {/* Category Selection - Responsive */}
        <div className="flex flex-wrap gap-1 sm:gap-2 justify-center mb-4 sm:mb-6 md:mb-8">
          {categories.map((cat) => (
            <button
              key={cat.key}
              className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${selectedCategory === cat.key ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-800 hover:bg-purple-200'}`}
              onClick={() => {
                setSelectedCategory(cat.key);
                setPage(1);
                setCurrentBatchIndex(0);
              }}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Navigation Buttons - Below Difficulty Selection */}
        <div className="flex justify-center mb-4 sm:mb-6">
          <div className="flex flex-row gap-2 items-center">
            <button
              onClick={() => window.location.href = '/memorize-words'}
              className="group relative px-3 sm:px-2 md:px-4 py-2 sm:py-1.5 md:py-2.5 bg-gradient-to-r from-purple-400 to-purple-500 text-white text-xs font-semibold rounded-full shadow-lg hover:from-purple-500 hover:to-purple-600 hover:shadow-purple-400/50 focus:ring-2 focus:ring-purple-300 transition-all duration-200 flex items-center justify-center hover:-translate-y-1 min-w-[80px] sm:min-w-[60px] md:min-w-[100px]"
            >
              <span className="hidden sm:inline">Memorize</span>
              <span className="sm:hidden">Memorize</span>
            </button>
            <button
              onClick={() => window.location.href = '/favorite-words'}
              className="group relative px-3 sm:px-2 md:px-4 py-2 sm:py-1.5 md:py-2.5 bg-gradient-to-r from-yellow-300 to-yellow-400 text-white text-xs font-semibold rounded-full shadow-lg hover:from-yellow-400 hover:to-yellow-500 hover:shadow-yellow-400/50 focus:ring-2 focus:ring-yellow-300 transition-all duration-200 flex items-center justify-center hover:-translate-y-1 min-w-[80px] sm:min-w-[60px] md:min-w-[100px]"
            >
              <span className="hidden sm:inline">Favorites</span>
              <span className="sm:hidden">Favorites</span>
            </button>
            <button
              onClick={() => window.location.href = '/completed-words'}
              className="group relative px-3 sm:px-2 md:px-4 py-2 sm:py-1.5 md:py-2.5 bg-gradient-to-r from-green-400 to-green-500 text-white text-xs font-semibold rounded-full shadow-lg hover:from-green-500 hover:to-green-600 hover:shadow-green-400/50 focus:ring-2 focus:ring-green-300 transition-all duration-200 flex items-center justify-center hover:-translate-y-1 min-w-[80px] sm:min-w-[60px] md:min-w-[100px]"
            >
              <span className="hidden sm:inline">Completed</span>
              <span className="sm:hidden">Completed</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎉</div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
              Congratulations!
            </h3>
            <p className="text-gray-600 text-sm sm:text-base">
              You have completed all words for this difficulty level.
            </p>
            <p className="text-gray-500 text-xs sm:text-sm mt-2">
              Great job! Keep practicing to maintain your skills.
            </p>
          </div>
        ) : (
          <div className="mb-8">
            {/* Count Display */}
            <div className="text-center mb-4">
              <p className="text-sm sm:text-base text-gray-600">
                Showing {completedCount} of {totalCount} items
              </p>
            </div>
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 justify-center items-stretch">
              {filteredWords.map((item, idx) => {
                const favoriteButton = (
                  <button
                    className={`px-3 sm:px-4 md:px-6 py-2 rounded-full text-xs sm:text-sm bg-gradient-to-r from-yellow-400 to-yellow-600 text-white font-semibold shadow-lg hover:from-yellow-500 hover:to-yellow-700 hover:scale-105 hover:shadow-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-300 ${isFavorite(item) ? 'bg-yellow-500 text-white cursor-not-allowed opacity-60' : ''}`}
                    onClick={async () => {
                      if (isFavorite(item)) return;
                      if (currentUser?.uid && item._id) {
                        // Optimistically update favoriteIds
                        setFavoriteIds(prev => [...prev, `word:${item._id}`]);
                        await api.addFavoriteWord(currentUser.uid, `word:${item._id}`, selectedCategory);
                        await refreshUserData();
                        // Refresh favoriteIds after favoriting
                        api.getFavoriteWords(currentUser.uid, selectedCategory).then(favs => {
                          setFavoriteIds(Array.isArray(favs) ? favs.map(f => f.itemId) : []);
                        });
                      }
                    }}
                    disabled={isFavorite(item)}
                    title={isFavorite(item) ? 'Already favorited' : 'Favorite'}
                  >
                    <span>⭐</span> <span className="hidden sm:inline">Favorite</span>
                  </button>
                );
                return (
                  <div key={item.en + idx} className="mb-4 w-full lg:w-1/3 bg-white rounded-xl shadow p-3 sm:p-4 flex flex-col gap-2">
                    <ItemCard
                      item={{
                        word: item.en,
                        en: item.en,
                        hi: item.hi,
                        bn: item.bn,
                        mr: item.mr,
                        te: item.te,
                        ta: item.ta,
                        gu: item.gu,
                        meaning: item.meaning || item.example
                      }}
                      type="word"
                      selectedLanguage={actualSelectedLanguage}
                      isFavorite={isFavorite(item)}
                      hideActions={true}
                      onDone={() => { }}
                      onMemorize={() => { }}
                      onFavorite={() => { }}
                    />
                    <div className="flex flex-wrap gap-1 sm:gap-2 mt-2 justify-center sm:justify-start">
                      <button
                        className="flex items-center gap-1 px-2 sm:px-3 md:px-6 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-green-400 to-green-600 text-white text-xs font-bold shadow-lg hover:scale-105 hover:shadow-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handleDone(item)}
                        disabled={loadingButtons[`done-${item._id}`]}
                      >
                        {loadingButtons[`done-${item._id}`] ? (
                          <>
                            <span className="animate-spin text-xs">⏳</span>
                            <span className="hidden sm:inline text-xs">Processing...</span>
                          </>
                        ) : (
                          <>
                            <span className="text-xs">✅</span>
                            <span className="text-xs">Done</span>
                          </>
                        )}
                      </button>
                      <button
                        className="flex items-center gap-1 px-2 sm:px-3 md:px-6 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white text-xs font-bold shadow-lg hover:scale-105 hover:shadow-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-pink-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handleMemorize(item)}
                        disabled={loadingButtons[`memorize-${item._id}`]}
                      >
                        {loadingButtons[`memorize-${item._id}`] ? (
                          <>
                            <span className="animate-spin text-xs">⏳</span>
                            <span className="hidden sm:inline text-xs">Processing...</span>
                          </>
                        ) : (
                          <>
                            <span className="text-xs">🧠</span>
                            <span className="text-xs">Memorize</span>
                          </>
                        )}
                      </button>
                      <button
                        className={`flex items-center gap-1 px-2 sm:px-3 md:px-6 py-1.5 sm:py-2 rounded-full text-xs font-bold shadow-lg hover:scale-105 hover:shadow-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-300 ${isFavorite(item) ? 'bg-yellow-500 text-white cursor-not-allowed opacity-60' : 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white hover:from-yellow-500 hover:to-yellow-700'}`}
                        onClick={async () => {
                          if (isFavorite(item)) return;
                          if (currentUser?.uid && item._id) {
                            // Optimistically update favoriteIds
                            setFavoriteIds(prev => [...prev, `word:${item._id}`]);
                            await api.addFavoriteWord(currentUser.uid, `word:${item._id}`, selectedCategory);
                            await refreshUserData();
                            // Refresh favoriteIds after favoriting
                            api.getFavoriteWords(currentUser.uid, selectedCategory).then(favs => {
                              setFavoriteIds(Array.isArray(favs) ? favs.map(f => f.itemId) : []);
                            });
                          }
                        }}
                        disabled={isFavorite(item)}
                        title={isFavorite(item) ? 'Already favorited' : 'Favorite'}
                      >
                        <span className="text-xs">⭐</span>
                        <span className="text-xs">Favorite</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Navigation Controls - Responsive */}
        <div className="flex justify-center items-center gap-2 sm:gap-4 mt-6 sm:mt-8">
          <button
            onClick={handlePrev}
            disabled={currentBatchIndex === 0 && page === 1}
            className="px-3 sm:px-4 py-2 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 text-white text-xs sm:text-sm font-bold shadow-lg hover:from-blue-500 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300"
          >
            ← <span className="hidden sm:inline">Previous</span>
          </button>
          <button
            onClick={handleNext}
            disabled={currentBatchIndex === filteredWords.length - 1 && page === totalPages}
            className="px-3 sm:px-4 py-2 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 text-white text-xs sm:text-sm font-bold shadow-lg hover:from-blue-500 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300"
          >
            <span className="hidden sm:inline">Next</span> →
          </button>
        </div>
      </div>
    </div>
  );
} 